import { useEffect, useMemo, useState } from "react";
import { api, yen } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useStore } from "../lib/store";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tags, Printer, CheckSquare, Square } from "lucide-react";

const esc = (v) =>
  String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const buildHtml = ({ rows, mode, store, ad, title }) => {
  const cells = rows
    .map(
      (p) => `<div class="lbl">
        <div class="store">${esc(store)}</div>
        <div class="name">${esc(p.name)}</div>
        ${p.weight ? `<div class="wt">${esc(p.weight)}</div>` : ""}
        <div class="price">${esc(yen(p.price))}<span class="unit"> / ${esc(p.unit || "pcs")}</span></div>
        ${ad ? `<div class="ad">${esc(ad)}</div>` : ""}
      </div>`
    )
    .join("");
  const a4 = `
    @page { size: A4; margin: 8mm; }
    .sheet { display: grid; grid-template-columns: repeat(3, 63mm); gap: 3mm; }
    .lbl { height: 33mm; }
    .name { font-size: 13px; }
    .price { font-size: 22px; }`;
  const thermal = `
    @page { size: 58mm auto; margin: 0; }
    .sheet { display: block; }
    .lbl { width: 52mm; margin: 0 auto 3mm; height: auto; padding: 3mm 2mm; }
    .name { font-size: 12px; }
    .price { font-size: 20px; }`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, "Hiragino Sans", sans-serif; color: #000; }
    .lbl { border: 1px dashed #999; border-radius: 2mm; padding: 2mm 3mm;
      display: flex; flex-direction: column; justify-content: space-between; page-break-inside: avoid; }
    .store { font-size: 8px; letter-spacing: .04em; text-transform: uppercase; color: #444; }
    .name { font-weight: 700; line-height: 1.15; }
    .price { font-weight: 700; font-variant-numeric: tabular-nums; }
    .unit { font-size: 10px; font-weight: 400; }
    .ad { font-size: 8px; color: #555; }
    .wt { font-size: 9px; color: #333; }
    ${mode === "a4" ? a4 : thermal}
  </style></head><body><div class="sheet">${cells}</div>
  <script>window.onload = function(){ window.print(); }</script>
  </body></html>`;
};

export default function LabelHarga() {
  const { t } = useI18n();
  const { settings, storeName } = useStore();
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState([]);
  const [mode, setMode] = useState("a4");
  const [q, setQ] = useState("");
  const [ad, setAd] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);
  useEffect(() => {
    setAd(settings?.label_ad_text || "");
  }, [settings?.label_ad_text]);

  const list = useMemo(
    () =>
      products.filter((p) =>
        q.trim() ? p.name.toLowerCase().includes(q.trim().toLowerCase()) : true
      ),
    [products, q]
  );
  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const print = () => {
    const rows = products.filter((p) => picked.includes(p.id));
    if (!rows.length) return;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) return;
    const html = buildHtml({ rows, mode, store: storeName, ad: ad.trim(), title: t("label_title") });
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    w.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="label-page">
      <div>
        <h1 className="font-display text-3xl font-semibold">{t("label_title")}</h1>
        <p className="text-sm text-muted-foreground">{t("label_sub")}</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <p className="font-display font-semibold flex items-center gap-2">
          <Tags size={18} className="text-primary" /> {t("label_options")}
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="grid grid-cols-2 gap-2">
            {[
              ["a4", t("label_format_a4")],
              ["thermal", t("label_format_58")],
            ].map(([m, label]) => (
              <button
                key={m}
                data-testid={`label-format-${m}`}
                onClick={() => setMode(m)}
                className={`py-2.5 rounded-xl text-sm font-medium border transition-colors duration-200 ${
                  mode === m
                    ? "bg-gray-900 text-white border-transparent"
                    : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <Input
            data-testid="label-ad-input"
            placeholder={t("label_ad")}
            value={ad}
            onChange={(e) => setAd(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            data-testid="label-search-input"
            placeholder={t("search_product")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
          />
          <Button
            variant="outline"
            data-testid="label-select-all-btn"
            onClick={() => setPicked(list.map((p) => p.id))}
          >
            <CheckSquare size={15} className="mr-1" /> {t("label_select_all")}
          </Button>
          <Button
            variant="outline"
            data-testid="label-clear-btn"
            onClick={() => setPicked([])}
          >
            <Square size={15} className="mr-1" /> {t("label_clear")}
          </Button>
          <span className="text-sm text-muted-foreground" data-testid="label-selected-count">
            {picked.length} {t("label_selected")}
          </span>
          <Button
            className="ml-auto h-11"
            data-testid="label-print-btn"
            disabled={picked.length === 0}
            onClick={print}
          >
            <Printer size={16} className="mr-2" /> {t("label_print")}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="label-product-list">
        {list.map((p) => {
          const on = picked.includes(p.id);
          return (
            <button
              key={p.id}
              data-testid={`label-pick-${p.id}`}
              onClick={() => toggle(p.id)}
              className={`text-left rounded-2xl border p-4 transition-all duration-200 ${
                on
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <p className="text-xs text-muted-foreground">{p.category}</p>
              <p className="font-medium leading-snug">{p.name}</p>
              <p className="num text-lg font-semibold mt-1">
                {yen(p.price)}
                <span className="text-xs text-muted-foreground font-normal"> / {p.unit}</span>
              </p>
            </button>
          );
        })}
        {list.length === 0 && <p className="text-sm text-muted-foreground">{t("no_data")}</p>}
      </div>
    </div>
  );
}
